<script lang="ts" setup>
import { useToast } from 'primevue/usetoast';
import { onMounted } from 'vue';
import { useRouter } from 'vue-router';
import {
  EnterNewPassword,
  EnterOtp,
  EnterUsername,
} from '~/components/auth/forms/forgot-password';
import type { ForgotPasswordStep } from '~/types/auth/forms/ForgotPasswordForm';
import {
  EnterNewPassword as EnterNewPasswordContent,
  EnterOtp as EnterOtpContent,
  EnterUsername as EnterUsernameContent,
  ForgotPasswordForm,
} from '~/utils/contents/auth/forms/ForgotPasswordForm';
import {
  ENTER_NEW_PASSWORD,
  ENTER_OTP,
  ENTER_USERNAME,
} from '~/utils/enums/auth/components/forms/ForgotPasswordSteps';

const router = useRouter();
const route = useRoute();
const form = ref<{
  email: string;
  token: string;
}>({
  email: route.query.email ? String(route.query.email) : '',
  token: '',
});
const currentStep = ref<ForgotPasswordStep>(ENTER_USERNAME);
const toast = useToast();

const currentStepIndex = computed((): number => {
  return ForgotPasswordForm.steps.findIndex(
    (step) => step.key === currentStep.value,
  );
});

const canGoNext = computed((): boolean => {
  return currentStepIndex.value < ForgotPasswordForm.steps.length - 1;
});

const goNext = (): void => {
  if (canGoNext.value) {
    const nextIndex = currentStepIndex.value + 1;
    const nextStep = ForgotPasswordForm.steps[nextIndex];

    if (nextStep) {
      currentStep.value = nextStep.key as ForgotPasswordStep;
    }
  } else {
    toast.add({
      severity: 'success',
      summary: ForgotPasswordForm.messages.success,
      life: 3000,
    });
    router.push('/auth/login');
  }
};

onMounted(() => {
  currentStep.value = ENTER_USERNAME;
  form.value.token = '';
});
</script>

<template>
  <div class="forgot-password-form border">
    <EnterUsername
      v-if="currentStep === ENTER_USERNAME"
      v-model="form.email"
      :initial-email="form.email"
      :data-testid="EnterUsernameContent.dataTestId"
      @success="goNext()"
    />
    <EnterOtp
      v-if="currentStep === ENTER_OTP"
      v-model="form.token"
      :email="form.email"
      :data-testid="EnterOtpContent.dataTestId"
      @success="goNext()"
    />
    <EnterNewPassword
      v-if="currentStep === ENTER_NEW_PASSWORD"
      :email="form.email"
      :token="form.token"
      :data-testid="EnterNewPasswordContent.dataTestId"
      @success="goNext()"
    />
  </div>
</template>

<style scoped lang="scss">
.forgot-password-form {
  width: 100%;
  max-width: 588px;
  padding: $spacing-xl;
  background-color: #fff;
  border-radius: $radius-lg;
  border: 1px solid #e6e6ea;
  margin-bottom: $spacing-xl;

  @media (min-width: 640px) {
    padding: $spacing-3xl;
  }

  @media (min-width: 768px) {
    padding: $spacing-5xl;
    margin-bottom: 0;
  }
}
</style>
